'use client';

import { RoundedCornerSizeType, UserVideo } from "@/lib/types";

/**
 * Calculate the corner radius based on the size and scale.
 * @param size
 * @param scale
 */
export const calculateCornerRadius = (size: RoundedCornerSizeType = 'none', scale: 1 | 0.75 | 0.5) => {
  switch (size) {
    case 'small':
      return 8 * scale + 'px';
    case 'medium':
      return 16 * scale + 'px';
    case 'large':
      return 32 * scale + 'px';
    default:
      return '0';
  }
};
export function PlayerStyle({
  video
}: {
  video: UserVideo;
}) {
  // Calculate the corner radius based on the size with fallback to old boolean value for rounded corners.
  const roundedCornersSize = video?.branding?.roundedCornersSize || (video?.branding?.roundedCorners ? 'medium' : 'none');
  let style = `
            :scope {
                /* Player Variables */                                
                --media-brand: ${video?.branding?.brandColor};
                --media-accent: ${video?.branding?.accentColor};
            
                /* Layout Variables */      
                --video-border-radius: ${calculateCornerRadius(roundedCornersSize, 1)}; 
                --video-border-radius-small: ${calculateCornerRadius(roundedCornersSize, 0.75)};
                --video-border-radius-xs: ${calculateCornerRadius(roundedCornersSize, 0.5)};
                --video-border: 5 solid ${video?.branding?.accentColor};
                --video-bg: #000000;
                
                /* Language Switcher */
                --language-switcher-button-bg: ${video?.branding?.menuBackgroundColor}66;
       
       
                /* Buttons. */
                --media-button-bg: rgba(0 0 0 / 0);
                --media-button-hover-bg: ${video?.branding?.brandColor};
                
                
                /* Poster. */
                --media-poster-bg: #000000;
            
                --video-brand: ${video?.branding?.brandColor};
                --video-controls-color: #f5f5f5;
                --video-focus-ring-color: rgb(78 156 246);
                --video-focus-ring: ${video?.branding?.accentColor} 3px var(--media-focus-ring-color);
                --video-font-family: sans-serif;
                --video-fullscreen-chapter-title-font-size: 16px;
                --video-gesture-seek-width: 20%;

                /* Buttons. */
                --big-play-button-bg: ${video?.branding?.centrePlayButton ? video?.branding?.brandColor : 'rgba(0 0 0 / 0)'};
                --big-play-button-bg-hover: ${video?.branding?.centrePlayButton ? video?.branding?.brandColor : 'rgba(0 0 0 / 0.4)'};
                --media-button-bg: rgba(0 0 0 / 0);
                --media-button-hover-bg: ${video?.branding?.brandColor};
                --media-button-hover-transform: scale(1.05);
                --media-button-border-radius: 8px;
                --media-button-icon-size: 75%;
                --media-button-size: 36px;
                --media-button-color: ${video?.branding?.accentColor};
                --video-sm-button-size: 32px;
                --video-sm-play-button-size: 40px;
                --video-sm-play-button-transform: translateY(25%);
                --video-sm-play-button-bg: rgba(0 0 0 / 0.6);
                
                /* Buffering */
                --media-buffering-track-fill-color: ${video?.branding?.brandColor};

                /* Sliders. */
                --video-sm-slider-focus-track-height: 2px;
                --video-slider-thumbnail-border: 2px solid ${video?.branding?.accentColor};
                --video-slider-thumbnail-border-radius: 12px;
                --video-volume-slider-max-width: 72px;

                /* Time. */
                --video-sm-start-duration-bg: rgba(0 0 0 / 0.64);
                --video-sm-start-duration-padding: 3px 6px;
                --video-sm-start-duration-color: var(--video-controls-color);
                --video-time-bg: unset;
                --video-fullscreen-time-font-size: 16px;

                /* Captions. */
                --caption-text-color: ${video?.branding?.captionTextColor || video?.branding?.accentColor};
                --caption-highlight-color: ${video?.branding?.captionHighlightColor || video?.branding?.brandColor}; 
                --video-sm-captions-offset: 48px;
                --video-captions-offset: 78px;
                --video-captions-transition: bottom 0.15s linear;
                --video-lg-fullscreen-captions-offset: 54px;
                
                /* Menu Text. */
                --media-menu-text-color: ${video?.branding?.menuTextColor};
                --media-menu-text-secondary-color: ${video?.branding?.menuTextColor}99;
        
                /* Menu. */
                --media-menu-bg: ${video?.branding?.menuBackgroundColor}99;
                --media-menu-border-radius: 8px;
                --media-menu-top-bar-bg: ${video?.branding?.menuBackgroundColor}44;
        
                /* Menu Section. */
                --media-menu-section-bg: ${video?.branding?.menuBackgroundColor}44;
                --media-menu-section-gap: 8px;
                --media-menu-section-border-radius: 8px;
                
                /* Menu Item. */
                --media-menu-item-border-radius: 8px;
                --media-menu-item-height: 44px;
                --media-menu-item-icon-size: 18px;
                --media-menu-item-icon-spacing: 4px;
                --media-menu-item-padding: 10px;                
            }`;
  //
  // const extendedStyle = `
  //    /* Layout Variables */
  //    .vds-audio-layout,
  //    .vds-video-layout {
  //        /* Shared. */
  //        --media-brand: #f5f5f5;
  //        --media-controls-color: #f5f5f5;
  //        --media-font-family: sans-serif;
  //
  //        /* Buffering. */
  //        --media-buffering-animation: vds-buffering-spin 1s linear infinite;
  //        --media-buffering-size: 96px;
  //        --media-buffering-track-color: #f5f5f5;
  //        --media-buffering-track-fill-color: var(--media-brand);
  //        --media-buffering-track-fill-offset: 50;
  //        --media-buffering-track-fill-opacity: 0.75;
  //        --media-buffering-track-fill-width: 9;
  //        --media-buffering-track-opacity: 0.25;
  //        --media-buffering-track-width: 8;
  //        --media-buffering-transition: opacity 200ms ease;
  //
  //        /* Buttons. */
  //        --media-button-border-radius: 8px;
  //        --media-button-color: var(--media-controls-color, #f5f5f5);
  //        --media-button-hover-bg: ${video?.branding?.brandColor};
  //        --media-button-hover-transform: scale(1.05);
  //        --media-button-hover-transition: transform 0.2s ease-in;
  //        --media-button-icon-size: 80%;
  //        --media-button-padding: 0px;
  //        --media-button-size: 40px;
  //        --media-button-touch-hover-bg: rgb(255 255 255 / 0.2);
  //        --media-button-touch-hover-border-radius: 100%;
  //        --media-sm-fullscreen-button-size: 42px;
  //        --media-fullscreen-button-size: 42px;
  //
  //        /* Tooltips. */
  //        --media-tooltip-bg-color: black;
  //        --media-tooltip-border-radius: 4px;
  //        --media-tooltip-border: 1px solid rgb(255 255 255 / 0.1);
  //        --media-tooltip-color: hsl(0, 0%, 80%);
  //        --media-tooltip-font-size: 13px;
  //        --media-tooltip-font-weight: 500;
  //        --media-tooltip-padding: 2px 8px;
  //        --media-tooltip-enter-animation: vds-tooltip-enter 0.2s ease-in;
  //        --media-tooltip-exit-animation: vds-tooltip-exit 0.2s ease-out;
  //
  //        /* Live Indicator. */
  //        --media-live-button-bg: #8a8a8a;
  //        --media-live-button-border-radius: 2px;
  //        --media-live-button-color: #161616;
  //        --media-live-button-edge-bg: #dc2626;
  //        --media-live-button-edge-color: #f5f5f5;
  //        --media-live-button-font-size: 12px;
  //        --media-live-button-font-weight: 600;
  //        --media-live-button-height: 40px;
  //        --media-live-button-letter-spacing: 1.5px;
  //        --media-live-button-padding: 1px 4px;
  //        --media-live-button-width: 40px;
  //
  //        /* Captions. */
  //        --media-captions-padding: 1%;
  //        --media-cue-backdrop: blur(8px);
  //        --media-cue-bg: rgba(0, 0, 0, 0.7);
  //        --media-cue-border-radius: 2px;
  //        --media-cue-border: unset;
  //        --media-cue-box-shadow: var(--cue-box-shadow);
  //        --media-cue-color: white;
  //        --media-cue-display-bg: unset;
  //        --media-cue-display-border-radius: unset;
  //        --media-cue-display-padding: unset;
  //        --media-cue-font-size: calc(var(--overlay-height) / 100 * 3);  /* --media-cue-font-size: calc(var(--overlay-height) / 100 * 4.5); */
  //        --media-cue-line-height: calc(var(--cue-font-size) * 1.2);
  //        --media-cue-padding-x: calc(var(--cue-font-size) * 0.4);
  //        --media-cue-padding-x: calc(var(--cue-font-size) * 0.6);
  //
  //        /* Chapter Title. */
  //        --media-chapter-title-color: rgba(255 255 255 / 0.64);
  //        --media-chapter-title-font-size: 14px;
  //        --media-chapter-title-font-weight: 500;
  //        --media-chapter-title-separator-color: var(--color);
  //        --media-chapter-title-separator-gap: 6px;
  //
  //        /* Controls. */
  //        --media-controls-padding: 0px;
  //        --media-controls-in-transition: opacity 0.2s ease-in;
  //        --media-controls-out-transition: opacity 0.2s ease-out;
  //
  //        /* Thumbnails. */
  //        --media-thumbnail-bg: black;
  //        --media-thumbnail-border: 1px solid white;
  //        --media-thumbnail-aspect-ratio: 16 / 9;
  //        --media-thumbnail-min-width: 120px;
  //        --media-thumbnail-min-height: calc(var(--media-thumbnail-min-width) / var(--aspect-ratio));
  //        --media-thumbnail-max-width: 180px;
  //        --media-thumbnail-max-height: calc(var(--media-thumbnail-max-width) / var(--aspect-ratio));
  //
  //        /* Time. */
  //        --media-time-bg: unset;
  //        --media-time-border-radius: unset;
  //        --media-time-border: unset;
  //        --media-time-color: #f5f5f5;
  //        --media-time-divider-color: #e0e0e0;
  //        --media-time-divider-gap: 2.5px;
  //        --media-time-font-size: 14px;
  //        --media-time-font-weight: 400;
  //        --media-time-letter-spacing: 0.025em;
  //
  //        /* Sliders. */
  //        --media-slider-width: 100%;
  //        --media-slider-height: 48px;
  //
  //        /* Slider Thumb. */
  //        --media-slider-thumb-bg: #fff;
  //        --media-slider-thumb-border-radius: 9999px;
  //        --media-slider-thumb-border: 1px solid #cacaca;
  //        --media-slider-thumb-size: 15px;
  //        --media-slider-thumb-transition: opacity 0.2s ease-in, box-shadow 0.2s ease;
  //
  //        /* Slider Tracks. */
  //        --media-slider-track-width: 100%;
  //        --media-slider-track-bg: rgb(255 255 255 / 0.3);
  //        --media-slider-track-border-radius: 1px;
  //        --media-slider-track-fill-bg: var(--media-brand);
  //        --media-slider-track-fill-live-bg: #dc2626;
  //        --media-slider-track-height: 5px;
  //        --media-slider-track-progress-bg: rgb(255 255 255 / 0.5);
  //        --media-slider-focused-thumb-shadow: 0 0 0 4px hsla(0, 0%, 100%, 0.4);
  //        --media-slider-focused-thumb-size: calc(var(--thumb-size) * 1.1);
  //        --media-slider-focused-track-height: calc(var(--track-height) * 1.25);
  //        --media-slider-focused-track-height: var(--track-height);
  //        --media-slider-focused-track-width: calc(var(--track-width) * 1.25);
  //        --media-slider-focused-track-width: var(--track-width);
  //
  //        /* Slider Steps. */
  //        --media-slider-step-width: 2.5px;
  //        --media-slider-step-color: rgb(124, 124, 124);
  //
  //        /* Slider Chapter. */
  //        --media-slider-chapter-hover-transform: scaleY(2);
  //        --media-slider-chapter-hover-transition: transform 0.1s cubic-bezier(0.4, 0, 1, 1);
  //
  //        /* Slider Preview. */
  //        --media-slider-preview-bg: unset;
  //        --media-slider-preview-border-radius: 2px;
  //
  //        /* Slider Chapter Title. */
  //        --media-slider-chapter-title-bg: unset;
  //        --media-slider-chapter-title-color: #f5f5f5;
  //        --media-slider-chapter-title-font-size: 14px;
  //        --media-slider-chapter-title-gap: 6px;
  //
  //        /* Slider Value. */
  //        --media-slider-value-bg: black;
  //        --media-slider-value-border-radius: 2px;
  //        --media-slider-value-border: unset;
  //        --media-slider-value-color: white;
  //        --media-slider-value-gap: 0px;
  //        --media-slider-value-padding: 1px 10px;
  //
  //        /* Menu Theme. */
  //        --media-menu-color-gray-50: rgb(245 245 245 / 0.1);
  //        --media-menu-color-gray-100: rgb(245 245 245 / 0.45);
  //        --media-menu-color-gray-200: rgb(10 10 10 / 0.6);
  //        --media-menu-color-gray-300: rgb(27 27 27);
  //
  //        /* Menu Text. */
  //        --media-menu-text-color: ${video?.branding?.menuTextColor};
  //        --media-menu-text-secondary-color: ${video?.branding?.menuTextColor}99;
  //
  //        /* Menu. */
  //        --media-menu-bg: ${video?.branding?.menuBackgroundColor}99;
  //        --media-menu-border-radius: 8px;
  //        --media-menu-border: 1px solid rgb(255 255 255 / 0.1);
  //        --media-menu-box-shadow: 1px 1px 1px rgb(10 10 10 / 0.5);
  //        --media-menu-divider: 1px solid var(--color-gray-50);
  //        --media-menu-font-size: 14px;
  //        --media-menu-font-weight: 500;
  //        --media-menu-max-height: 250px;
  //        --media-menu-min-width: 220px;
  //        --media-menu-padding: 12px;
  //        --media-menu-top-bar-bg: ${video?.branding?.menuBackgroundColor}44;
  //        --media-menu-arrow-icon-size: 18px;
  //        --media-menu-icon-rotate-deg: 90deg;
  //
  //        --media-menu-enter-animation: vds-menu-enter 0.3s ease-out;
  //        --media-menu-exit-animation: vds-menu-exit 0.2s ease-out;
  //
  //        --media-menu-scrollbar-track-bg: transparent;
  //        --media-menu-scrollbar-thumb-bg: var(--color-gray-50);
  //
  //        --media-sm-menu-landscape-max-height: min(70vh, 400px);
  //        --media-sm-menu-portrait-max-height: 40vh;
  //
  //        /* Menu Section. */
  //        --media-menu-section-bg: ${video?.branding?.menuBackgroundColor}44;
  //        --media-menu-section-border: unset;
  //        --media-menu-section-divider: var(--divider);
  //        --media-menu-section-header-font-size: 12px;
  //        --media-menu-section-header-font-weight: 500;
  //        --media-menu-section-gap: 8px;
  //        --media-menu-section-border-radius: 8px;
  //
  //        /* Menu Item. */
  //        --media-menu-item-bg: transparent;
  //        --media-menu-item-border-radius: 8px;
  //        --media-menu-item-border: 0;
  //        --media-menu-item-height: 40px;
  //        --media-menu-item-hover-bg: var(--color-gray-50);
  //        --media-menu-item-icon-size: 18px;
  //        --media-menu-item-icon-spacing: 6px;
  //        --media-menu-item-padding: 10px;
  //
  //        /* Menu Radio. */
  //        --media-menu-radio-icon-color: var(--text-color);
  //
  //        /* Menu Checkbox. */
  //        --media-menu-checkbox-width: 40px;
  //        --media-menu-checkbox-height: 18px;
  //        --media-menu-checkbox-bg-active: #1ba13f;
  //        --media-menu-checkbox-bg: var(--color-gray-100);
  //        --media-menu-checkbox-handle-bg: #f5f5f5;
  //        --media-menu-checkbox-handle-border: unset;
  //        --media-menu-checkbox-handle-diameter: calc(var(--checkbox-height) - 2px);
  //
  //        /* Menu Slider. */
  //        --media-menu-slider-height: 32px;
  //        --media-menu-slider-track-bg: var(--color-gray-50);
  //        --media-menu-slider-track-fill-bg: var(--color-inverse);
  //
  //        /* Menu Hint. */
  //        --media-menu-hint-color: var(--text-secondary-color);
  //        --media-menu-hint-font-size: 13px;
  //        --media-menu-hint-font-weight: 400;
  //
  //        /* Chapters Menu. */
  //        --media-chapters-divider: var(--divider);
  //        --media-chapters-duration-bg: unset;
  //        --media-chapters-duration-border-radius: 2px;
  //        --media-chapters-focus-padding: 4px;
  //        --media-chapters-item-active-bg: var(--color-gray-50);
  //        --media-chapters-item-active-border-left: unset;
  //        --media-chapters-min-width: var(--media-menu-min-width, 220px);
  //        --media-chapters-padding: 0;
  //        --media-chapters-progress-bg: var(--color-inverse);
  //        --media-chapters-progress-border-radius: 0;
  //        --media-chapters-progress-height: 4px;
  //        --media-chapters-start-time-border-radius: 2px;
  //        --media-chapters-start-time-letter-spacing: 0.4px;
  //        --media-chapters-start-time-padding: 1px 4px;
  //        --media-chapters-thumbnail-border: 0;
  //        --media-chapters-thumbnail-gap: 12px;
  //        --media-chapters-thumbnail-max-height: 68px;
  //        --media-chapters-thumbnail-max-width: 120px;
  //        --media-chapters-thumbnail-min-height: 56px;
  //        --media-chapters-thumbnail-min-width: 100px;
  //        --media-chapters-time-font-size: 12px;
  //        --media-chapters-time-font-weight: 500;
  //        --media-chapters-time-gap: 6px;
  //        --media-chapters-with-thumbnails-min-width: 300px;
  //    }`

  style = style.replace(/\n/g, '').replace(/\s+/g, ' ');
  return <style type="text/css" data-sentry-component="PlayerStyle" data-sentry-source-file="PlayerStyle.tsx">{style}</style>;
}